import { useTranslation } from 'react-i18next';
import type { OwnerAppointment } from '@/api/appointments/appointmentsApi.types';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import {
  buildAppointmentStatusTranslationKey,
  buildTherapyTypeTranslationKey,
} from '@/constants/appointmentOptions';
import { cn } from '@/lib/utils';
import { formatIsoDateToDayAndMonth } from '@/utils/dateFormatting';

const CANCELLED_STATUS = 'cancelled';
const CLIENT_CANCELLATION_SOURCE = 'client';

interface VisitSummaryRowProps {
  visit: OwnerAppointment;
}

export function VisitSummaryRow({ visit }: VisitSummaryRowProps) {
  const { t } = useTranslation(['clients', 'appointments']);
  const isCancelled = visit.status === CANCELLED_STATUS;
  const statusLabel = isCancelled
    ? visit.cancelledBy === CLIENT_CANCELLATION_SOURCE
      ? t('clients:visits.cancelledByYou')
      : t('clients:visits.cancelledByClinic')
    : t(buildAppointmentStatusTranslationKey(visit.status));
  const statusTone: BadgeTone = isCancelled ? 'danger' : 'muted';

  return (
    <div
      className={cn(
        'p-2.5 border border-zooborder rounded-xl flex items-center justify-between gap-3',
        isCancelled ? 'bg-zoooffwhite' : ''
      )}
    >
      <div className="min-w-0">
        <div
          className={cn(
            'text-[13px] font-bold truncate',
            isCancelled ? 'text-zoomuted line-through' : 'text-zooindigo'
          )}
        >
          {visit.patientName}
        </div>
        <div className="text-[11px] text-zoomuted truncate">
          {t(buildTherapyTypeTranslationKey(visit.therapyType))}
        </div>
        <Badge tone={statusTone} size="sm" className="mt-1">
          {statusLabel}
        </Badge>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[13px] font-bold text-zoomuted">
          {formatIsoDateToDayAndMonth(visit.date)}
        </div>
        <div className="text-[11px] text-zoomuted">{visit.startTime}</div>
      </div>
    </div>
  );
}
